import { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  useConversations,
  useCreateConversation,
  useDeleteConversation,
  pinnedFirst,
} from '../../hooks/useConversations';
// SearchPanel is intentionally NOT mounted in v1 per product direction v2
// ("Search: Omit initially"). Component file + backend `/search` endpoint
// remain in the codebase for the v2 unhide.
// import { SearchPanel } from '../search/SearchPanel';
import { useAuth } from '../../auth/AuthContext';
import { useCan } from '../../auth/useCan';
import { useConfirm } from '../dialog/ConfirmDialog';
import { useToast } from '../toast/Toast';

export function Sidebar({ onNavigate }: { onNavigate?: () => void }) {
  const { data: conversations = [] } = useConversations();
  const { mutateAsync: createConv, isPending } = useCreateConversation();
  const { mutateAsync: deleteConv } = useDeleteConversation();
  const { user, logout } = useAuth();
  const can = useCan();
  const navigate = useNavigate();
  const location = useLocation();
  const { conversationId: activeId } = useParams<{ conversationId: string }>();
  const { confirm } = useConfirm();
  const toast = useToast();

  /**
   * Delete a conversation from the sidebar.
   *
   * Triggered by the trailing × button on each ConvLink. Shows a
   * confirmation modal in-place (no route navigation) and deletes
   * on confirm.
   *
   * Navigation rules after delete:
   *   - User is viewing some OTHER conversation → stay there.
   *   - User is viewing the deleted conversation AND has at least one
   *     other conversation → jump to the next-most-recent one. This is
   *     the "don't kick me out of the session" fix — instead of dumping
   *     the user back at the persona picker (HomePage), keep them in
   *     a working conversation chrome with their sidebar context.
   *   - User is viewing the deleted conversation AND it was the last
   *     one → go to /, since there's nothing else to navigate to.
   */
  async function handleDeleteConv(id: string, title: string): Promise<void> {
    const ok = await confirm({
      title: 'Delete conversation',
      description: `"${title || 'Untitled'}" — this action cannot be undone.`,
      confirmLabel: 'Delete',
      cancelLabel: 'Cancel',
      destructive: true,
    });
    if (!ok) return;
    try {
      await deleteConv(id);
      toast.show('Conversation deleted.', 'success');
      if (activeId === id || location.pathname.startsWith(`/c/${id}`)) {
        // Find the next conversation to land on. `sorted` is already
        // pinned-first then updatedAt-desc, so the first non-deleted
        // entry is the freshest sibling.
        const fallback = sorted.find((c) => c.id !== id);
        if (fallback) {
          navigate(`/c/${fallback.id}`);
        } else {
          navigate('/');
        }
      }
    } catch (err) {
      toast.show(
        `Could not delete: ${(err as Error).message ?? 'unknown error'}`,
        'error',
      );
    }
  }

  // Persona-scoped sidebar:
  // - In a CFO conversation → only show CFO conversations.
  // - In a COO conversation → only show COO conversations.
  // - In a CEO conversation → show ALL conversations across executives
  //   (the CEO is the cross-functional view by design).
  // - Outside any conversation (briefing, admin) → show all (no active
  //   "session" to scope to).
  // We derive the active persona from the active conversation in the
  // already-loaded list — avoids an extra fetch.
  const activeConv = activeId ? conversations.find((c) => c.id === activeId) : undefined;
  const activePersona = activeConv?.personaId;
  const visibleConversations =
    activePersona && activePersona !== 'ceo'
      ? conversations.filter((c) => c.personaId === activePersona)
      : conversations;

  const sorted = pinnedFirst(visibleConversations);
  const pinned = sorted.filter((c) => c.pinned);
  const recent = sorted.filter((c) => !c.pinned).slice(0, 30);

  async function newChat() {
    // "New conversation" inherits the persona of the conversation the
    // user is currently in. No active conversation → CFO default.
    // CEO conversations spawn another CEO conversation (not CFO) so the
    // user stays in the cross-functional lens.
    const personaForNew = activePersona ?? 'cfo';
    const r = await createConv(personaForNew);
    navigate(`/c/${r.conversation.id}`);
    onNavigate?.();
  }

  // Persona tint for the sidebar background. Same cream base as the
  // ConversationView body — the sidebar reads as the SAME session
  // surface as the body, with the same persona color showing through,
  // and the white cards float on top of that shared backdrop.
  const tint = activePersona ? SIDEBAR_TINTS[activePersona] : undefined;

  return (
    <aside
      className="flex h-full w-full min-h-0 flex-col gap-2 overflow-y-auto overflow-x-hidden border-r border-slate-900/[0.06] p-2.5"
      style={{
        // Cream base matches the conversation body exactly (#f4f3ee) so
        // there's no white "column" feel — the sidebar is part of the
        // same session room.
        backgroundColor: '#f4f3ee',
        // Persona accent — single full-column wash so the color is
        // visibly there without going saturated. Stronger than the
        // body's radial because the sidebar is a narrow strip and a
        // diluted tint disappears at small surface area.
        backgroundImage: tint
          ? `linear-gradient(180deg, ${tint} 0%, ${tint} 100%)`
          : undefined,
      }}
    >
      {/* Card 1 — Brand header (Walker Blue logo + wordmark). Padding
          py-4 + h-10 logo align this card height with the dashboard
          header (which has py-4 + a 3-line title block). The
          conversation header already matched at this height; bringing
          the sidebar to py-4 makes it consistent across both routes. */}
      <SidebarCard>
        <div className="flex items-center gap-3 px-3.5 py-4">
          <img
            src="/walker-blue-logo.png"
            alt=""
            className="h-10 w-auto shrink-0"
            aria-hidden="true"
          />
          <div className="min-w-0">
            <div className="brand-eyebrow leading-none">Walker Blue</div>
            <div className="brand-wordmark mt-1 truncate text-sm leading-tight">
              Intelligence Hub
            </div>
          </div>
        </div>
      </SidebarCard>

      {/* Card 2 — Home */}
      <SidebarCard>
        <Link
          to="/"
          onClick={onNavigate}
          className="flex items-center gap-2.5 rounded-2xl px-3.5 py-2.5 text-sm font-medium text-slate-800 transition-colors hover:bg-slate-900/[0.05]"
        >
          <HomeIcon />
          <span>Home</span>
        </Link>
      </SidebarCard>

      {/* Card 3 — Executive switcher (dropdown) */}
      <SidebarCard padded={false}>
        <ExecutiveSwitcher onNavigate={onNavigate} createConv={createConv} />
      </SidebarCard>

      {/* Card 3b — COO abilities (Tickets / Integrations). Preview features
          gated by real RBAC (server enforces too — this is UX only) AND scoped
          to the COO context: shown on Home / briefing / admin (no active
          persona) and inside a COO conversation, but hidden inside a CFO / CMO /
          CEO session (those aren't COO abilities). */}
      {(can('tickets.view') || can('integrations.dashboard.view')) &&
        (!activePersona || activePersona === 'coo') && (
        <SidebarCard padded={false}>
          <div className="px-3 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">
            COO abilities
          </div>
          <ul className="pb-1.5">
            {can('tickets.view') && (
              <li>
                <Link
                  to="/tickets"
                  onClick={onNavigate}
                  className="flex items-center gap-2.5 px-3.5 py-2 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-900/[0.05]"
                >
                  <TicketIcon />
                  <span>Tickets</span>
                  <span className="ml-auto rounded-full bg-slate-100 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-slate-500">
                    Preview
                  </span>
                </Link>
              </li>
            )}
            {can('integrations.dashboard.view') && (
              <li>
                <Link
                  to="/integrations"
                  onClick={onNavigate}
                  className="flex items-center gap-2.5 px-3.5 py-2 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-900/[0.05]"
                >
                  <FlowIcon />
                  <span>Integrations</span>
                  <span className="ml-auto rounded-full bg-slate-100 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-slate-500">
                    Preview
                  </span>
                </Link>
              </li>
            )}
          </ul>
        </SidebarCard>
      )}

      {/* Card 4 — New conversation CTA. Filled because it's the
          single most-used action in the sidebar. */}
      <button
        type="button"
        onClick={() => void newChat()}
        disabled={isPending}
        className="flex items-center justify-center gap-2 rounded-2xl bg-slate-900 px-3.5 py-3 text-sm font-semibold text-white shadow-sm transition-all hover:bg-slate-800 active:scale-[0.99] disabled:cursor-not-allowed disabled:opacity-60"
      >
        <PlusIcon />
        <span>{isPending ? 'Starting…' : 'New conversation'}</span>
      </button>

      {/* Card 5 — Recent (and pinned). Scrollable inner; the card is
          flexible so it grows to fill remaining height. */}
      <SidebarCard className="flex flex-1 min-h-0 flex-col" padded={false}>
        <div className="flex-1 space-y-5 overflow-y-auto px-3 py-3">
          {pinned.length > 0 && (
            <Section title="Pinned">
              {pinned.map((c) => (
                <ConvLink
                  key={c.id}
                  id={c.id}
                  title={c.title}
                  active={activeId === c.id}
                  onClick={onNavigate}
                  onDelete={() => void handleDeleteConv(c.id, c.title)}
                  pinned
                />
              ))}
            </Section>
          )}

          <Section title="Recent">
            {recent.length === 0 && (
              <p className="px-2.5 py-2 text-xs text-slate-400">
                No conversations yet.
              </p>
            )}
            {recent.map((c) => (
              <ConvLink
                key={c.id}
                id={c.id}
                title={c.title}
                active={activeId === c.id}
                onClick={onNavigate}
                onDelete={() => void handleDeleteConv(c.id, c.title)}
              />
            ))}
          </Section>
        </div>
      </SidebarCard>

      {/* Card 6 — User identity + sign out + trust signal + co-brand */}
      <SidebarCard>
        {user && (
          <div className="space-y-2.5 px-3 py-3">
            <div className="flex items-center gap-2.5">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-slate-900/[0.08] text-xs font-semibold text-slate-700">
                {user.name
                  .split(' ')
                  .map((n) => n[0])
                  .slice(0, 2)
                  .join('')
                  .toUpperCase()}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5">
                  <span className="truncate text-sm font-medium text-slate-900">
                    {user.name}
                  </span>
                  {user.isAdmin && (
                    <span
                      className="inline-flex items-center rounded bg-slate-900/[0.08] px-1.5 text-[10px] font-semibold uppercase tracking-wide text-slate-700 ring-1 ring-slate-900/[0.10]"
                      title="You have admin permissions"
                    >
                      Admin
                    </span>
                  )}
                </div>
                <div className="truncate text-xs text-slate-500">{user.email}</div>
              </div>
              <button
                type="button"
                onClick={() => void logout()}
                className="btn-ghost h-8 min-h-0 px-2 text-xs"
                aria-label="Sign out"
                title="Sign out"
              >
                Sign out
              </button>
            </div>

            {user.isAdmin && (
              <Link
                to="/admin"
                onClick={onNavigate}
                className="flex items-center gap-2 rounded-md px-2.5 py-2 text-xs font-medium text-slate-600 hover:bg-slate-100 hover:text-slate-900"
              >
                <SettingsIcon />
                <span>System dashboard</span>
              </Link>
            )}

            <div className="flex items-center justify-center gap-1.5 border-t border-slate-100 pt-2.5 text-[10px] uppercase tracking-[0.12em] text-slate-400">
              <span
                className="inline-block h-1.5 w-1.5 rounded-full bg-emerald-500"
                aria-hidden="true"
              />
              <span>Read-only · audit-logged</span>
            </div>

            <CoBrandCarousel />
          </div>
        )}
      </SidebarCard>
    </aside>
  );
}

// =============================================================================
// CoBrandCarousel — static Walker Blue mark in the sidebar footer.
// Filename kept for import compatibility; the cross-fade between WB
// and a partner mark was removed when co-branding was retired.
// =============================================================================

function CoBrandCarousel() {
  return (
    <div className="relative flex h-5 items-center justify-center">
      <img
        src="/walker-blue-logo.png"
        alt="Walker Blue"
        className="h-5 w-auto opacity-70"
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// SidebarCard — neutral white block with a hairline border + soft shadow.
// Each "section" of the sidebar (brand, home, switcher, recents, user) wraps
// in one of these so the chrome reads as a stack of independent cards over
// the persona-tinted cream backdrop.
// ---------------------------------------------------------------------------

function SidebarCard({
  children,
  className = '',
  padded = true,
}: {
  children: React.ReactNode;
  className?: string;
  /** When false, the card has no built-in padding — children render flush
   *  to its edges (used for the switcher + recents which manage their own
   *  inner spacing). */
  padded?: boolean;
}) {
  return (
    <div
      // White cards floating on the persona-tinted session backdrop.
      // The contrast (white card on tinted surface) is what defines
      // each recuadro now — borders + shadow only assist.
      className={`rounded-2xl border border-slate-900/[0.06] bg-white shadow-[0_1px_2px_rgba(12,26,43,0.04)] ${
        padded ? '' : ''
      } ${className}`}
    >
      {children}
    </div>
  );
}

// Persona-keyed sidebar tint. Same hue family as the ConversationView
// body's bodyTint, but applied as a full-column wash (not a radial) and
// at higher alpha so the persona color is actually visible on a narrow
// strip. The visual effect is "lighter version of the session color" —
// you can tell at a glance which executive's room you're in just from
// the sidebar wash, before reading any text. Keep these in sync with
// PERSONA_THEMES in ConversationView.tsx (same hue families).
const SIDEBAR_TINTS: Record<string, string> = {
  cfo: 'rgba(74, 130, 199, 0.0512)', // wb-500 light (-40% from 0.14 per Nelson 2026-05-08)
  coo: 'rgba(45, 212, 191, 0.0512)', // teal-400 light
  cmo: 'rgba(167, 139, 250, 0.0512)', // cmo-400 light
  ceo: 'rgba(245, 158, 11, 0.0512)', // amber-500 light
};

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="section-label mb-2 px-2.5">{title}</div>
      <ul className="space-y-0.5" role="list">
        {children}
      </ul>
    </div>
  );
}

function ConvLink({
  id,
  title,
  active,
  pinned,
  onClick,
  onDelete,
}: {
  id: string;
  title: string;
  active: boolean;
  pinned?: boolean;
  onClick?: () => void;
  onDelete?: () => void;
}) {
  return (
    <li className="group/conv relative">
      <Link
        to={`/c/${id}`}
        onClick={onClick}
        className={`flex items-center gap-2 rounded-md px-3 py-2 pr-9 text-sm transition-colors ${
          active
            ? 'bg-slate-900/[0.08] font-medium text-slate-900 ring-1 ring-slate-900/[0.10]'
            : 'text-slate-700 hover:bg-slate-900/[0.04]'
        }`}
      >
        {pinned && <PinIcon />}
        <span className="truncate">{title || 'Untitled'}</span>
      </Link>
      {onDelete && (
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onDelete();
          }}
          className={`absolute right-1.5 top-1/2 -translate-y-1/2 inline-flex h-6 w-6 items-center justify-center rounded text-slate-400 transition-all hover:bg-red-50 hover:text-red-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-300 ${
            active ? 'opacity-100' : 'opacity-0 group-hover/conv:opacity-100 focus-visible:opacity-100'
          }`}
          aria-label={`Delete conversation "${title || 'Untitled'}"`}
          title="Delete conversation"
        >
          <CloseIcon />
        </button>
      )}
    </li>
  );
}

function CloseIcon() {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

function PlusIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <line x1="12" y1="5" x2="12" y2="19" />
      <line x1="5" y1="12" x2="19" y2="12" />
    </svg>
  );
}

function PinIcon() {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="currentColor"
      className="text-slate-400"
      aria-hidden="true"
    >
      <path d="M16 3l5 5-5 5v3l-3 3-5-5H4l5-5V6l3-3h4z" />
    </svg>
  );
}

// =============================================================================
// ExecutiveSwitcher — dropdown that lets the user switch persona from
// anywhere in the app without going back to the HomePage. Click triggers
// the dropdown; clicking an active persona creates a fresh conversation
// for that persona. Coming-soon personas show but are disabled (no click
// action — visual signal only).
//
// At the bottom of the dropdown, a "View aggregated briefing" item routes
// to /briefing (the cross-conversation diligence cockpit). Keeps the
// briefing page reachable now that the sidebar's old "Executive briefing"
// button has been replaced with this switcher.
// =============================================================================

interface SwitcherPersona {
  id: 'cfo' | 'coo' | 'cmo' | 'ceo';
  label: string;
  subtitle: string;
  active: boolean;
  dotClass: string;
}

const SWITCHER_PERSONAS: SwitcherPersona[] = [
  { id: 'cfo', label: 'CFO', subtitle: 'Chief Financial Officer', active: true, dotClass: 'bg-wb-700' },
  { id: 'coo', label: 'COO', subtitle: 'Chief Operating Officer', active: true, dotClass: 'bg-teal-600' },
  { id: 'cmo', label: 'CMO', subtitle: 'Chief Marketing Officer', active: false, dotClass: 'bg-cmo-600' },
  { id: 'ceo', label: 'CEO', subtitle: 'Chief Executive Officer', active: true, dotClass: 'bg-amber-600' },
];

function ExecutiveSwitcher({
  onNavigate,
  createConv,
}: {
  onNavigate?: () => void;
  createConv: (personaId: string) => Promise<{ conversation: { id: string } }>;
}) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const navigate = useNavigate();
  const wrapperRef = useRef<HTMLDivElement>(null);

  // Close when clicking outside the dropdown.
  useEffect(() => {
    if (!open) return;
    function onClick(e: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  async function handlePersonaPick(p: SwitcherPersona): Promise<void> {
    if (!p.active) return;
    if (busy) return;
    setBusy(p.id);
    try {
      const r = await createConv(p.id);
      setOpen(false);
      navigate(`/c/${r.conversation.id}`);
      onNavigate?.();
    } catch {
      // useCreateConversation surfaces errors via React Query toasts in
      // ConversationView etc.; nothing to swallow here.
    } finally {
      setBusy(null);
    }
  }

  return (
    <div ref={wrapperRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between gap-2 rounded-2xl bg-transparent px-3.5 py-2.5 text-sm font-medium text-slate-800 transition-colors hover:bg-slate-900/[0.05]"
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <span className="flex items-center gap-2">
          <BriefingIcon />
          <span>Executive briefing</span>
        </span>
        <ChevronIcon open={open} />
      </button>

      {open && (
        <div
          role="menu"
          className="absolute left-0 right-0 top-full z-30 mt-1 overflow-hidden rounded-md border border-slate-200 bg-white shadow-lg"
        >
          <div className="px-3 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">
            Switch executive
          </div>
          <ul className="py-1">
            {SWITCHER_PERSONAS.map((p) => (
              <li key={p.id}>
                <button
                  type="button"
                  role="menuitem"
                  disabled={!p.active || busy === p.id}
                  onClick={() => void handlePersonaPick(p)}
                  className={`flex w-full items-center gap-2 px-3 py-2 text-left text-sm transition-colors ${
                    p.active
                      ? 'text-slate-800 hover:bg-slate-100'
                      : 'cursor-not-allowed text-slate-400'
                  }`}
                  aria-disabled={!p.active}
                >
                  <span
                    className={`inline-block h-2 w-2 shrink-0 rounded-full ${p.dotClass} ${p.active ? '' : 'opacity-50'}`}
                    aria-hidden="true"
                  />
                  <span className="flex-1">
                    <span className="font-semibold">{p.label}</span>
                    <span className="ml-1.5 text-xs text-slate-500">— {p.subtitle}</span>
                  </span>
                  {!p.active && (
                    <span className="rounded-full bg-slate-100 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-slate-500">
                      Soon
                    </span>
                  )}
                  {busy === p.id && (
                    <span className="text-[10px] uppercase tracking-wide text-slate-500">Starting…</span>
                  )}
                </button>
              </li>
            ))}
          </ul>
          <div className="border-t border-slate-100">
            <Link
              to="/briefing"
              role="menuitem"
              onClick={() => {
                setOpen(false);
                onNavigate?.();
              }}
              className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-slate-700 transition-colors hover:bg-slate-100"
            >
              <BriefingIcon />
              <span>View aggregated briefing</span>
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}

function ChevronIcon({ open }: { open: boolean }) {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className={`text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`}
    >
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}

function HomeIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="text-wb-700"
    >
      <path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
      <polyline points="9 22 9 12 15 12 15 22" />
    </svg>
  );
}

function BriefingIcon() {
  // 4-square dashboard glyph — matches the dashboard tab icon for visual rhyme
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="text-wb-700"
    >
      <rect x="3" y="3" width="7" height="9" />
      <rect x="14" y="3" width="7" height="5" />
      <rect x="14" y="12" width="7" height="9" />
      <rect x="3" y="16" width="7" height="5" />
    </svg>
  );
}

function TicketIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="text-teal-600"
    >
      <path d="M3 9a3 3 0 0 0 0 6v2a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-2a3 3 0 0 1 0-6V7a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2z" />
      <path d="M13 5v14" />
    </svg>
  );
}

function FlowIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="text-violet-600"
    >
      <circle cx="5" cy="6" r="2.5" />
      <circle cx="19" cy="6" r="2.5" />
      <circle cx="12" cy="18" r="2.5" />
      <path d="M7.5 6h9M6 8.2l4.6 7.4M18 8.2l-4.6 7.4" />
    </svg>
  );
}

function SettingsIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33h.01a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82v.01a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </svg>
  );
}
